import { alpha, Box, IconButton, Stack, TableCell, TableHead, TableRow, TextFieldProps, Theme, Typography } from '@mui/material';
import { FC, Fragment } from 'react';

import { RHFSwitch, RHFTextField, uuidv4 } from '@zeta-core';
import { CloseIcon } from '@zeta-core/assets';
import CommonTableLayout from './common-table-layout';
import { renderComponent } from './render-component';

export interface IFieldType {
  label?: string;
  clientField?: string;
  partnerField?: string;
  component: JSX.ElementType;
  options?: any[];
  thousandSeparator?: boolean;
  fixedDecimalScale?: boolean;
  decimalScale?: number;
  type?: TextFieldProps['type'];
  InputProps?: TextFieldProps['InputProps'];
  children?: IFieldType[];
  onChange?: (event: Date) => void;
  textAlign?: 'right' | 'left';
  nestedFieldsFlag?: boolean;
  multiple?: boolean;
  nestedFields?: IFieldType[];
  chargesField?: boolean;
  charges?: IFieldType[];
  row?: boolean;
}

export const FieldsHead = [
  { label: 'Details', width: 100, colSpan: 2 },
  { label: 'Client', minWidth: 136 },
  { label: 'Partner', minWidth: 136 },
];

const renderChildRows = (field: IFieldType) =>
  field.children?.map((child: IFieldType, index: number) => (
    <TableRow key={child.label}>
      {index === 0 && (
        <TableCell rowSpan={field.children?.length}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ py: 2.063, px: 1.75 }}>
            {field.label}
          </Typography>
        </TableCell>
      )}
      <TableCell>
        <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
          {child.label}
        </Typography>
      </TableCell>
      <TableCell>{renderComponent(child, child.clientField!)}</TableCell>
      <TableCell>{renderComponent(child, child.partnerField!)}</TableCell>
    </TableRow>
  ));

const renderChargesFields = (field: IFieldType, theme: Theme) => {
  return (
    <TableRow>
      <TableCell colSpan={2}>
        <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
          {field.label}
        </Typography>
      </TableCell>
      <TableCell
        sx={{
          '&.MuiTableCell-root': {
            padding: '0 !important',
          },
        }}
      >
        <TableHead sx={{ display: 'flex', width: '100%' }}>
          <TableCell sx={{ display: 'block', width: '50%' }}>Initial Charge</TableCell>
          <TableCell sx={{ display: 'block', width: '50%' }}>Ongoing Charge</TableCell>
        </TableHead>
        <TableCell sx={{ display: 'flex', width: '100%', padding: '0!important', borderBottom: 'none !important' }}>
          <Box sx={{ display: 'block', width: '50%', borderRight: `1px solid ${alpha(theme.palette.grey[500], 0.16)} !important` }}>
            {renderComponent(field, `${field.clientField}.initialCharge`)}
          </Box>
          <Box sx={{ display: 'block', width: '50%', borderRight: `1px solid ${alpha(theme.palette.grey[500], 0.16)} !important` }}>
            {renderComponent(field, `${field.clientField}.ongoingCharge`)}
          </Box>
        </TableCell>
      </TableCell>
      <TableCell
        sx={{
          '&.MuiTableCell-root': {
            padding: '0 !important',
            borderRight: 'none !important',
          },
        }}
      >
        <TableHead sx={{ display: 'flex', width: '100%' }}>
          <TableCell sx={{ display: 'block', width: '50%' }}>Initial Charge</TableCell>
          <TableCell sx={{ display: 'block', width: '50%' }}>Ongoing Charge</TableCell>
        </TableHead>
        <TableCell sx={{ display: 'flex', width: '100%', padding: '0!important', borderBottom: 'none !important' }}>
          <Box sx={{ display: 'block', width: '50%', borderRight: `1px solid ${alpha(theme.palette.grey[500], 0.16)} !important` }}>
            {renderComponent(field, `${field.partnerField}.initialCharge`)}
          </Box>
          <Box sx={{ display: 'block', width: '50%', borderRight: `1px solid ${alpha(theme.palette.grey[500], 0.16)} !important` }}>
            {renderComponent(field, `${field.partnerField}.ongoingCharge`)}
          </Box>
        </TableCell>
      </TableCell>
    </TableRow>
  );
};

export const renderFields = (field: IFieldType, theme: Theme) => {
  if (field?.nestedFieldsFlag) {
    return (
      <TableRow>
        <TableCell colSpan={2}>
          <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
            {field.label}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack spacing={2} padding={2}>
            {field.nestedFields?.map((nestedField: IFieldType) =>
              renderComponent(nestedField, nestedField?.clientField || '', {
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                flex: 1,
              })
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack spacing={2} padding={2}>
            {field.nestedFields?.map((nestedField: IFieldType) =>
              renderComponent(nestedField, nestedField?.partnerField || '', {
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                flex: 1,
              })
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  }

  if (field.children) {
    return <>{renderChildRows(field)}</>;
  }

  if (field.chargesField) {
    return <>{renderChargesFields(field, theme)}</>;
  }

  return (
    <TableRow key={field.label} sx={{ borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.16)} !important`, verticalAlign: 'top' }}>
      <TableCell colSpan={2}>
        <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
          {field.label}
        </Typography>
      </TableCell>
      <TableCell>{renderComponent(field, field.clientField!)}</TableCell>
      <TableCell>{renderComponent(field, field.partnerField!)}</TableCell>
    </TableRow>
  );
};

export interface IGeneralFieldType {
  label?: string;
  name: string;
  component: JSX.ElementType;
  type?: TextFieldProps['type'];
  InputProps?: TextFieldProps['InputProps'];
  options?: any[];
  multiple?: boolean;
  row?: boolean;
  disabled?: boolean;
  sxProp?: object;
  show?: boolean; // configure to show row or column
  useGetSetMethods?: boolean;
  shouldDirty?: boolean;
  hideBorder?: boolean;
}

export const renderGeneralFields = (field: IGeneralFieldType, fieldNameFront?: string, showEmptyCell = true, isReadOnly = false) => {
  const disabled = isReadOnly || field.disabled;
  const fieldPrefix = fieldNameFront && fieldNameFront?.length > 0 ? `${fieldNameFront}.${field.name}` : field.name;

  const fieldComponent = renderComponent(field, fieldPrefix, {}, disabled);
  return (
    field.show && (
      <TableRow key={field.label} sx={{ borderBottom: `1px solid ${alpha('#919EAB', 0.16)} !important`, verticalAlign: 'top' }}>
        <TableCell colSpan={2}>
          <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }} id={field.label}>
            {field.label}
          </Typography>
        </TableCell>
        <TableCell sx={{ '&.MuiTableCell-root': { borderRightColor: showEmptyCell ? 'initial' : 'transparent !important' } }}>
          <Stack>{fieldComponent}</Stack>
        </TableCell>
        {showEmptyCell && <TableCell></TableCell>}
      </TableRow>
    )
  );
};
interface RenderArrayFieldWithChildrenProps {
  fieldsArray: Array<any>;
  fieldLabel: string;
  fieldArrayType: IGeneralFieldType[];
  currentTab?: string;
  onRemove: (index: number) => void;
  arrayName: string;
  isClient: boolean;
  isReadOnly: boolean;
  showToggle?: boolean;
  toggleFieldName?: string;
  disableEditAddress?: boolean;
}

export const RenderArrayFieldWithChildren: FC<RenderArrayFieldWithChildrenProps> = ({
  fieldsArray,
  fieldLabel,
  fieldArrayType,
  currentTab,
  onRemove,
  arrayName,
  isClient,
  isReadOnly,
  showToggle,
  toggleFieldName,
  disableEditAddress,
}) => {
  return fieldsArray.map((field, fieldIndex: number) => {
    return fieldArrayType?.map((child: IGeneralFieldType, index: number) => (
      <TableRow key={child.label}>
        {index === 0 && (
          <TableCell rowSpan={fieldArrayType?.length}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ py: 2.063, px: 1.75 }} id={`${arrayName}-${fieldIndex + 1}`}>
              {`${arrayName} - ${fieldIndex + 1}`}
              {fieldIndex === 0 && showToggle && (
                <RHFSwitch
                  disableTypography
                  name={toggleFieldName}
                  label={
                    <Typography variant="button" textAlign="start" color={'text.primary'} id={toggleFieldName}>
                      Use same address as Client
                    </Typography>
                  }
                  labelPlacement="end"
                />
              )}
            </Typography>
          </TableCell>
        )}
        {child.show && (
          <TableCell>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}
              id={`${child.label}+${fieldIndex + 1}`}
            >
              {child.label + ' '}
            </Typography>
          </TableCell>
        )}
        {child.show && currentTab && (
          <TableCell>
            {renderComponent(
              child,
              `${currentTab}.${fieldLabel}.${fieldIndex}.${child.name}`,
              {},
              isClient || isReadOnly || (fieldIndex === 0 && disableEditAddress && currentTab === 'partner')
            )}
          </TableCell>
        )}
        {child.show && !currentTab && (
          <TableCell>
            {renderComponent(
              child,
              `${fieldLabel}.${fieldIndex}.${child.name}`,
              {},
              isClient || isReadOnly || (fieldIndex === 0 && disableEditAddress && currentTab === 'partner')
            )}
          </TableCell>
        )}
        {index === 0 && !isReadOnly && (
          <TableCell rowSpan={fieldArrayType?.length}>
            <Stack justifyContent="center" alignItems="center">
              {fieldsArray.length > 1 && (
                <IconButton onClick={() => onRemove(fieldIndex)} id={`remove-icon-${fieldIndex + 0}`}>
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>
          </TableCell>
        )}
      </TableRow>
    ));
  });
};

interface RenderArrayFieldWithActionProps {
  fieldsArray: Array<any>;
  fieldArrayType: IGeneralFieldType[];
  onRemove: (index: number) => void;
  arrayName: string;
  fieldNameFront: string;
  headLabel?: string;
}

export const RenderArrayFieldWithAction: FC<RenderArrayFieldWithActionProps> = ({
  fieldsArray,
  fieldArrayType,
  onRemove,
  arrayName,
  fieldNameFront,
  headLabel = '',
}) => {
  return (
    <Stack spacing={2}>
      {fieldsArray.map((field, fieldIndex: number) => (
        <CommonTableLayout
          tableHead={[
            {
              label: `${arrayName} - ${fieldIndex + 1}`,
              colSpan: 2,
              width: '50%',
              show: true,
            },
            {
              label: (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography>{headLabel}</Typography>
                  {fieldsArray.length > 1 && (
                    <IconButton onClick={() => onRemove(fieldIndex)} sx={{ p: 0 }}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </Stack>
              ),
              show: true,
            },
          ]}
          sx={{ borderRadius: '12px' }}
        >
          <>{fieldArrayType.map((fields: any) => renderGeneralFields(fields, `${fieldNameFront}.${fieldIndex}`, false))}</>
        </CommonTableLayout>
      ))}
    </Stack>
  );
};

interface RenderGeneralFieldsWithSubsectionProps {
  fields: Array<any>;
  arrayName: string;
}

export const RenderGeneralFieldsWithSubsection: FC<RenderGeneralFieldsWithSubsectionProps> = ({ fields, arrayName }) => {
  return (
    <CommonTableLayout
      tableHead={[
        { label: 'Information', width: '50%', colSpan: 2, show: true },
        { label: 'Details', minWidth: '50%', show: true },
      ]}
      sx={{ borderRadius: '12px' }}
    >
      <>
        {fields.map((field) => {
          if (field.hasSubsection && field.show) {
            return (
              <Fragment key={uuidv4()}>
                {field?.subFields?.map((child, index) => (
                  <TableRow key={uuidv4()}>
                    {index === 0 && (
                      <TableCell rowSpan={field.subFields.length}>
                        <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75 }}>
                          {field.label}
                        </Typography>
                      </TableCell>
                    )}

                    {child.show && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
                          {child.label}
                        </Typography>
                      </TableCell>
                    )}

                    {child.show && <TableCell>{renderComponent(child, `${arrayName}.${field.name}.${child.name}`)}</TableCell>}
                  </TableRow>
                ))}
              </Fragment>
            );
          }
          return renderGeneralFields(field, `${arrayName}`, false);
        })}
      </>
    </CommonTableLayout>
  );
};

interface RenderGeneralFieldsVerticalProps {
  fieldsArray: Array<any>;
  tableDetails: IGeneralFieldType[];
  tableHead: any;
  disableLastCell?: boolean;
  lastCellName?: string;
  fieldArrayName: string;
}

export const RenderGeneralFieldsVertical: FC<RenderGeneralFieldsVerticalProps> = ({
  tableDetails,
  fieldsArray,
  fieldArrayName,
  tableHead,
  lastCellName,
  disableLastCell = false,
}) => {
  return (
    <Stack spacing={2}>
        <CommonTableLayout tableHead={tableHead} sx={{ borderRadius: '12px' }}>
          <>
            {tableDetails.map((field, fieldIndex: number) => (
              <TableRow key={field.label} sx={{ borderBottom: `1px solid ${alpha('#919EAB', 0.16)} !important`, verticalAlign: 'top' }}>
                <TableCell colSpan={2}>
                  <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
                    {field.label}
                  </Typography>
                </TableCell>

                {fieldsArray.map((e, index) => (
                  <TableCell>{renderComponent(field, `${fieldArrayName}.${index}.details.${field.name}`, field.sxProp ?? {}, field.disabled)}</TableCell>
                ))}

                <TableCell>
                  <RHFTextField name={`${lastCellName}.${field.name}`} disabled={disableLastCell} />
                </TableCell>
              </TableRow>
            ))}
          </>
        </CommonTableLayout>
      </Stack>
  );
};

export const renderFieldsWithQuestion = (field: IGeneralFieldType, fieldNameFront?: string) => {
  return (
    <TableRow key={field.label} sx={{ borderBottom: `1px solid ${alpha('#919EAB', 0.16)} !important`, verticalAlign: 'top' }}>
      {field.show && (
        <>
          <TableCell>
            <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
              {field.label}
            </Typography>
          </TableCell>
          <TableCell sx={{ verticalAlign: 'middle' }}>
            {renderComponent(field, fieldNameFront && fieldNameFront?.length > 0 ? `${fieldNameFront}.${field.name}` : field.name, {}, field.disabled)}
          </TableCell>
        </>
      )}

      {field.questionObj?.show && (
        <>
          <TableCell>
            <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
              {field.questionObj?.label || ''}
            </Typography>
          </TableCell>
          <TableCell sx={{ verticalAlign: 'middle' }}>{renderComponent(field?.questionObj, field?.questionObj?.name, {}, field.disabled)}</TableCell>
        </>
      )}
    </TableRow>
  );
};
